import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import candidatevetanalysis from '../../apis/candidatescreening/candidatevetanalysis';
import getvetviolations from '../../apis/vettest/getvetviolations';
import { UserTypeContext } from "../../AppRoute";
import Videomanualcontroller from '../videomanualcontroller/videomanualcontroller';
import Playicon from '../Icons/playicon';
import Tooltip from '@mui/material/Tooltip';
import ArrowBack from '@mui/icons-material/ArrowBack';
const jsonConfig = require("../../Config.json");

const CandidateAnalysis = () => {
    const { id } = useParams();
    const history = useNavigate();
    const location = useLocation();
    const { vetTestId, jdId } = location.state || {};
    const { userRecords } = useContext(UserTypeContext);
    const [analysisData, setAnalysisData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [interviewstatus, setInterviewstatus] = useState("");
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [leftHoveredIndex, setLeftHoveredIndex] = useState(null);
    const [PlotComponent, setPlotComponent] = useState(null);
    const [violationsData, setViolationsData] = useState(null);

    const [selectedViolationTimestamp, setSelectedViolationTimestamp] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const data = [
        {
            label: "Sentence Structure",
            value: (analysisData?.overallParameterScores ?? []).find(p => p.name === "Sentence Structure")?.gseScore?.toFixed(1) ?? 0,
            color: "#1f77b4"
        },
        {
            label: "Vocabulary",
            value: (analysisData?.overallParameterScores ?? []).find(p => p.name === "Vocabulary")?.gseScore?.toFixed(1) ?? 0,
            color: "#198f8f"
        },
        {
            label: "Fluency",
            value: (analysisData?.overallParameterScores ?? []).find(p => p.name === "Fluency")?.gseScore?.toFixed(1) ?? 0,
            color: "#6a35a8"
        },
        {
            label: "Pronunciation",
            value: (analysisData?.overallParameterScores ?? []).find(p => p.name === "Pronunciation")?.gseScore?.toFixed(1) ?? 0,
            color: "#b31b6b"
        },
    ];

    const getViolations = async () => {
        try {
            let response = await getvetviolations(id);
            return response?.data?.data ?? null;
        } catch (error) {
            console.error('Error fetching vet violations:', error);
            return null;
        }
    }

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const gotoDashBoard = () => {
        history('/jdcandidates', {
            state: { toggleAiScreen: false, vetTestId: vetTestId, jdId: jdId },
        })
    }



    useEffect(() => {
        const fetchAnalysisData = async () => {
            try {
                let payload = {
                    vetCandidateTestId: id,
                    recruitmentpartnerId: userRecords?.recruitmentpartnerId
                }
                const response = await candidatevetanalysis(payload);
                const data = response?.data?.data ?? null;
                setAnalysisData(data);
                setInterviewstatus(data?.statusdetails?.code || "");
                setLoading(false);
            } catch (err) {
                setLoading(false);
                console.error('Error fetching candidate analysis:', err);
            }
        };
        fetchAnalysisData();

        const fetchViolationsData = async () => {
            const data = await getViolations();
            setViolationsData(data);
        };
        fetchViolationsData();
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadPlotly = async () => {
            try {
                const [plotlyModule, factoryModule] = await Promise.all([
                    import('plotly.js-cartesian-dist-min'),
                    import('react-plotly.js/factory'),
                ]);

                if (!isMounted) {
                    return;
                }

                const Plotly = plotlyModule.default ?? plotlyModule;
                const createPlotlyComponent = factoryModule.default ?? factoryModule;
                setPlotComponent(() => createPlotlyComponent(Plotly));
            } catch (err) {
                console.error('Error loading Plotly:', err);
            }
        };

        loadPlotly();

        return () => {
            isMounted = false;
        };
    }, []);

    const gseScore = (() => {
        const scores = (analysisData?.overallParameterScores ?? [])
            .filter(p =>
                p.name === "Sentence Structure" ||
                p.name === "Vocabulary" ||
                p.name === "Fluency" ||
                p.name === "Pronunciation"
            )
            .map(p => Number(p.gseScore ?? 0));

        if (scores.length === 0) return 0;

        const totalSum = scores.reduce((sum, score) => sum + score, 0);
        return Number((totalSum / scores.length).toFixed(1));
    })();

    const formatDuration = (secs) => {
        if (secs == null || isNaN(secs)) return '00:00';
        const s = Math.floor(Number(secs));
        const hours = Math.floor(s / 3600);
        const mins = Math.floor((s % 3600) / 60);
        const seconds = s % 60;
        if (hours > 0) {
            return `${hours}:${mins.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${mins}:${seconds.toString().padStart(2, '0')}`;
    };

    const formatDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const getStatusClass = () => {
        if (interviewstatus === 'completed') {
            return 'status-completed';
        } else if (interviewstatus === 'initiated') {
            return 'status-technical-glitch';
        } else if (interviewstatus === 'interrupted') {
            return 'status-candidate-interrupt';
        }
        return 'status-unknown';
    };

    if (loading) {
        return (
            <div className='loading_interview_analysis'>
                <CircularProgress />
            </div>
        );
    }

    return (
        <>
            <div className='maincontainer-interview-analysis'>

                <div className='top-container-interview-analysis'>
                    <div className="reportHeader_jdCandidateList">
                        <Tooltip title={"Go Back to Candidate List"} placement="bottom">
                            <div className="goBackButton_jdCandidateList" onClick={(e) => gotoDashBoard()}>
                                <ArrowBack sx={{ fontSize: 20 }} />
                            </div>
                        </Tooltip>
                        <div className="reportTitle_jdCandidateList">Analysis</div>
                    </div>
                </div>
                <div className='candidate-info-card'>
                    <div className='left-container-candidateinfo'>
                        <span className='candidate-name'>{analysisData?.name}</span>
                        <span className='sub-items-candidate-info'>{analysisData?.email}</span>
                        <div className='interview-info-container'>
                            <span className='sub-items-candidate-info'>Position : {analysisData?.testName}</span>
                            <span className='sub-items-candidate-info'>Interview Date : {formatDate(analysisData?.testGivenAt)}</span>
                            <span className='sub-items-candidate-info'>Duration : {formatDuration(analysisData?.totalDuration)}</span>
                        </div>
                        <div className='interview-status-container'>
                            <span className='status-label'>Interview Status:</span>
                            <span className={`status-badge ${getStatusClass()}`}>
                                {analysisData?.testStatus}
                            </span>
                        </div>
                    </div>

                    <div className="vetanalysis">
                        <div className="assessment-container">
                            <div className="rating-summary-column">
                                <div
                                    onMouseEnter={() => setLeftHoveredIndex(0)}
                                    onMouseLeave={() => setLeftHoveredIndex(null)}
                                    className={`score-card-large summary-card ${leftHoveredIndex === 0 ? 'card-active-hover' : ''}`}
                                >
                                    <span className="score-card-title-large">GSE Score</span>
                                    <span className="score-card-value-large font-navy">
                                        {Number(analysisData?.overallGseScore ?? 0).toFixed(1)}
                                    </span>
                                    {analysisData?.overallCfbrlevel ? (
                                        <span className="score-card-badge-large">
                                            {analysisData.overallCfbrlevel}
                                        </span>
                                    ) : (
                                        <div className="score-card-spacer-large" />
                                    )}
                                </div>

                                <div
                                    onMouseEnter={() => setLeftHoveredIndex(1)}
                                    onMouseLeave={() => setLeftHoveredIndex(null)}
                                    className={`score-card-large summary-card ${leftHoveredIndex === 1 ? 'card-active-hover' : ''}`}
                                >
                                    <span className="score-card-title-large">CFBR Level</span>
                                    <span className="score-card-value-large font-navy">
                                        {analysisData?.overallCfbrlevel ?? 'B3'}
                                    </span>
                                    <div className="score-card-spacer-large" />
                                </div>
                            </div>

                            <div className="scores-grid-matrix">
                                {(analysisData?.overallParameterScores ?? [])
                                    .filter(item =>
                                        item.name === "Sentence Structure" ||
                                        item.name === "Vocabulary" ||
                                        item.name === "Fluency" ||
                                        item.name === "Pronunciation"
                                    )
                                    .map((item, index) => (
                                        <div
                                            key={index}
                                            onMouseEnter={() => setHoveredIndex(index)}
                                            onMouseLeave={() => setHoveredIndex(null)}
                                            className={`score-card-large ${hoveredIndex === index ? 'card-active-hover' : ''}`}
                                        >
                                            <span className={`score-card-title-large ${hoveredIndex === index ? 'title-expanded' : ''}`}>
                                                {item.name}
                                            </span>

                                            <span className="score-card-value-large font-navy">
                                                {Number(item.gseScore ?? 0).toFixed(0)}
                                            </span>

                                            {item.cfbrLevel ? (
                                                <span className="score-card-badge-large">
                                                    {item.cfbrLevel}
                                                </span>
                                            ) : (
                                                <div className="score-card-spacer-large" />
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>


                {/* Violations Accordion with clickable timestamps */}
                {violationsData && violationsData.length > 0 && (
                    <Accordion className='violations-accordion' expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="violations-content"
                            id="violations-header"
                            className='violations-accordion-summary'
                        >
                            <Typography component="span" className='violations-title'>
                                Interview Violations ({violationsData.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails className='violations-accordion-details'>
                            {(() => {
                                const groupedViolations = violationsData.reduce((acc, violation) => {
                                    const source = violation.violationSource || 'OTHER';
                                    if (!acc[source]) {
                                        acc[source] = [];
                                    }
                                    acc[source].push(violation);
                                    return acc;
                                }, {});

                                return Object.entries(groupedViolations).map(([source, violations]) => (
                                    <div key={source} className='violation-category'>
                                        <div className='violation-category-header'>
                                            <span className='violation-dot'></span>
                                            <span className='violation-category-title'>
                                                {source} Violations ({violations.length})
                                            </span>
                                        </div>
                                        {violations.map((violation, index) => (
                                            <div key={`${source}-${index}`} className='violation-item'>
                                                <div className='violation-info'>
                                                    <div className='violation-text'>
                                                        {violation.violationType}
                                                    </div>
                                                    <div
                                                        className='violation-timestamp clickable'
                                                        title="Click to seek video to this timestamp"
                                                    >
                                                        <span className="timestamp-icon cp" onClick={() => { setSelectedViolationTimestamp(violation.atSec); setExpanded(false) }}><Playicon fill='black' /></span>
                                                        Detected at : {formatDuration(violation.atSec)}
                                                    </div>
                                                </div>
                                                <span className={`violation-severity severity-${violation.severity?.toLowerCase() || 'low'}`}>
                                                    {violation.severity || 'LOW'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ));
                            })()}
                        </AccordionDetails>
                    </Accordion>
                )}

                <div className='interview-content-container'>
                    <div className='left-side-container-interview-infos'>
                        <div className='interview-video-container'>
                            <span className='jd-heading'>Interview Video</span>
                            <Videomanualcontroller
                                src={`${jsonConfig.ctResourcesCdn}/vetinterview/${id}/video/${id}.${analysisData?.extension || 'webm'}` || ''}
                                interviewDuration={analysisData?.totalDuration || 0}
                                violationAtSec={selectedViolationTimestamp}
                            />
                        </div>
                        <div className='interview-video-container mt_20'>
                            {!PlotComponent ? (
                                <div className='loading_interview_analysis'>
                                    <CircularProgress />
                                </div>
                            ) : (
                                <PlotComponent
                                    data={[
                                        {
                                            type: "bar",
                                            x: data.map((d) => d.label),
                                            y: data.map((d) => d.value),
                                            marker: {
                                                color: data.map((d) => d.color),
                                            },
                                            text: data.map((d) => d.value),
                                            textposition: "inside",
                                            insidetextanchor: "end",
                                            textfont: {
                                                color: "#fff",
                                                size: 14,
                                            },
                                            hoverinfo: "none",
                                        },
                                    ]}
                                    layout={{
                                        width: 360,
                                        height: 500,
                                        margin: {
                                            l: 70,
                                            r: 30,
                                            t: 30,
                                            b: 140,
                                        },
                                        yaxis: {
                                            range: [10, 90],
                                            dtick: 10,
                                            gridcolor: "#d9d9d9",
                                            zeroline: false,
                                            title: {
                                                text: "GSE",
                                            },
                                        },
                                        xaxis: {
                                            tickfont: {
                                                size: 11,
                                            },
                                            automargin: true,
                                        },
                                        shapes: [
                                            {
                                                type: "line",
                                                x0: -0.5,
                                                x1: data.length - 0.5,
                                                y0: gseScore,
                                                y1: gseScore,
                                                line: {
                                                    color: "#f28c28",
                                                    width: 3,
                                                },
                                            },
                                        ],
                                        annotations: [
                                            {
                                                x: -0.45,
                                                y: gseScore,
                                                text: String(gseScore),
                                                showarrow: false,
                                                bgcolor: "#fff",
                                                bordercolor: "#f28c28",
                                                borderwidth: 2,
                                                borderpad: 4,
                                                font: {
                                                    color: "#333",
                                                    size: 12,
                                                },
                                            },
                                        ],
                                    }}
                                    config={{
                                        displayModeBar: false,
                                        responsive: true,
                                    }}
                                    style={{ width: "60%" }}
                                />
                            )}
                        </div>
                    </div>

                    <div className="vetanalysis_question">
                        <div className="question-answer-analysis-container">
                            <Typography className="section-by-question-analysis-title" variant="h5">
                                Section-by-Question Analysis
                            </Typography>
                            {(() => {
                                const sectionNames = {
                                    VS01: "Read Aloud",
                                    VS02: "Repeat Sentence",
                                    VS03: "Speaking Responses",
                                    VS04: "Sentence Reordering",
                                    VS05: "Language Proficiency & Vocabulary",
                                    VS06: "Written Expression"
                                };

                                const groupedSections = analysisData?.answers?.reduce((acc, currentItem) => {
                                    const code = currentItem.rawCode || "Unknown";
                                    if (!acc[code]) {
                                        acc[code] = {
                                            sectionName: sectionNames[code] || `Section ${code}`,
                                            questions: []
                                        };
                                    }
                                    acc[code].questions.push(currentItem);
                                    return acc;
                                }, {});

                                const structuredSections = Object.values(groupedSections || {});

                                return structuredSections.map((section, sectionIndex) => (
                                    <Accordion key={sectionIndex} className="section-container-accordion mb_20">
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="section-summary-header">
                                            <Typography className="section-title">
                                                {section.sectionName}
                                            </Typography>
                                        </AccordionSummary>

                                        <AccordionDetails className="section-details-content">
                                            {section.questions?.map((qa, index) => {
                                                const displayScore = qa.score ?? qa.rating ?? qa.gseScore;
                                                return (
                                                    <Accordion key={qa.questionId || index} className="qa-accordion">
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon />}
                                                            aria-controls={`panel-${sectionIndex}-${index}-content`}
                                                            id={`panel-${sectionIndex}-${index}-header`}
                                                            className="qa-accordion-summary"
                                                        >
                                                            <div className="accordion-header-content">
                                                                <div>
                                                                    <Typography component="span" className="question-number">
                                                                        Q{index + 1}
                                                                    </Typography>
                                                                    <Typography component="span" className="question-text">
                                                                        {qa.question}
                                                                    </Typography>
                                                                </div>

                                                                {displayScore != null && (
                                                                    <div className="question-meta">
                                                                        <span className="question-rating">
                                                                            {displayScore.toFixed(1)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </AccordionSummary>

                                                        <AccordionDetails className="qa-accordion-details">
                                                            <div className="answer-container">
                                                                <Typography component="div" className="answer-label">
                                                                    Answer:
                                                                </Typography>

                                                                <Typography component="div" className="answer-text">
                                                                    {(() => {
                                                                        if (!qa.answer) return "No answer provided";
                                                                        try {
                                                                            if (typeof qa.answer === 'string' && qa.answer.startsWith('[') && qa.answer.endsWith(']')) {
                                                                                return JSON.parse(qa.answer).join(" ");
                                                                            }
                                                                            return qa.answer;
                                                                        } catch (e) {
                                                                            return qa.answer;
                                                                        }
                                                                    })()}
                                                                </Typography>

                                                                {qa.audioFname && (
                                                                    <div className="audio-listen-wrapper">
                                                                        <Typography component="div" className="audio-label">
                                                                            Listen to Audio:
                                                                        </Typography>
                                                                        <audio controls className="mt_5">
                                                                            <source
                                                                                src={`${jsonConfig?.ctResourcesCdn || ''}/vetinterview/${id}/audio/${qa.audioFname}`}
                                                                                type="audio/mpeg"
                                                                            />
                                                                        </audio>
                                                                    </div>
                                                                )}

                                                                {qa.parameterScores && qa.parameterScores.length > 0 && (
                                                                    <div className="parameter-scores-breakdown">
                                                                        <Typography variant="body2" className="breakdown-title">
                                                                            Metrics Breakdown {qa.cfbrLevel ? `(${qa.cfbrLevel})` : ''}:
                                                                        </Typography>
                                                                        <div className="breakdown-badges-grid">
                                                                            {qa.parameterScores.map((param, pIdx) => (
                                                                                <div key={pIdx} className="breakdown-badge-item">
                                                                                    <strong>{param.name}:</strong> {Number(param.score).toFixed(1)}%
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            })}
                                        </AccordionDetails>
                                    </Accordion>
                                ));
                            })()}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CandidateAnalysis;